import { CartAddEvent } from '@theme/events';

class BagNotification {
  constructor(root) {
    this.root = root;
    this.desktopPanel = root.querySelector('.bag-notification__desktop');
    this.mobilePanel = root.querySelector('.bag-notification__mobile');
    this.itemsContainer = root.querySelector('[data-bag-items]');
    this.countElement = root.querySelector('[data-bag-count]');
    this.subtotalElement = root.querySelector('[data-bag-subtotal]');
    this.deliveryWrap = root.querySelector('[data-bag-delivery]');
    this.deliveryElement = root.querySelector('[data-bag-delivery-message]');
    this.deliveryProgressBar = root.querySelector('[data-bag-delivery-progress-bar]');

    this.mobileDeliveryWrap = root.querySelector('[data-bag-mobile-delivery]');
    this.mobileDeliveryElement = root.querySelector('[data-bag-mobile-delivery-message]');
    this.mobileDeliveryProgressBar = root.querySelector('[data-bag-mobile-delivery-progress-bar]');
    this.closeButtons = root.querySelectorAll('[data-bag-close]');
    this.deleteIconMarkup = root.querySelector('[data-bag-delete-icon]')?.innerHTML?.trim() || '';

    this.moneyFormat = root.dataset.moneyFormat || '£{{amount}}';
    this.currency = root.dataset.currency || 'GBP';
    this.cartUrl = root.dataset.cartUrl || '/cart';
    this.freeDeliveryThreshold = Number(root.dataset.freeDeliveryThreshold || 4000);
    this.freeDeliveryShowFrom = Number(root.dataset.freeDeliveryShowFrom || 3000);

    this.mobileMediaQuery = window.matchMedia('(max-width: 749.999px)');
    this.desktopTimeout = 10000;
    this.mobileTimeout = 3000;
    this.storageKey = 'bagNotificationRecentKeys';
    this.timer = null;
    this.previousCart = null;
    this.isHoveredOrFocused = false;
    this.mobileMinimumVisibleUntil = 0;

    this.handleCartAdd = this.handleCartAdd.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
    this.handleWindowReposition = this.handleWindowReposition.bind(this);
    this.close = this.close.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
  }

  async init() {
    this.closeButtons.forEach((button) => button.addEventListener('click', this.close));
    this.root.addEventListener('click', this.handleClick);

    document.addEventListener('pointerdown', this.handleDocumentPointerDown, true);
    window.addEventListener('resize', this.handleWindowReposition, { passive: true });
    window.addEventListener('scroll', this.handleWindowReposition, { passive: true });

    if (this.desktopPanel) {
      this.desktopPanel.addEventListener('mouseenter', this.handleMouseEnter);
      this.desktopPanel.addEventListener('mouseleave', this.handleMouseLeave);
      this.desktopPanel.addEventListener('focusin', this.handleFocusIn);
      this.desktopPanel.addEventListener('focusout', this.handleFocusOut);
    }

    if (this.mobilePanel) {
      this.mobilePanel.addEventListener('focusin', this.handleFocusIn);
      this.mobilePanel.addEventListener('focusout', this.handleFocusOut);
    }

    document.addEventListener(CartAddEvent.eventName, this.handleCartAdd);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });

    this.updateAnchorPosition();
    this.previousCart = await this.fetchCart().catch(() => null);

    if (this.previousCart) {
      this.syncHeaderCartBubble(this.previousCart);
    }
  }

  destroy() {
    this.closeButtons.forEach((button) => button.removeEventListener('click', this.close));
    this.root.removeEventListener('click', this.handleClick);

    document.removeEventListener('pointerdown', this.handleDocumentPointerDown, true);
    window.removeEventListener('resize', this.handleWindowReposition);
    window.removeEventListener('scroll', this.handleWindowReposition);

    if (this.desktopPanel) {
      this.desktopPanel.removeEventListener('mouseenter', this.handleMouseEnter);
      this.desktopPanel.removeEventListener('mouseleave', this.handleMouseLeave);
      this.desktopPanel.removeEventListener('focusin', this.handleFocusIn);
      this.desktopPanel.removeEventListener('focusout', this.handleFocusOut);
    }

    if (this.mobilePanel) {
      this.mobilePanel.removeEventListener('focusin', this.handleFocusIn);
      this.mobilePanel.removeEventListener('focusout', this.handleFocusOut);
    }

    document.removeEventListener(CartAddEvent.eventName, this.handleCartAdd);

    window.clearTimeout(this.timer);
  }

  get cartJsonUrl() {
    const rootPath = window.Shopify?.routes?.root || '/';
    return `${rootPath}cart.js`;
  }

  get fallbackCartChangeUrl() {
    if (window.Theme?.routes?.cart_change_url) return window.Theme.routes.cart_change_url;

    const rootPath = window.Shopify?.routes?.root || '/';
    return `${rootPath}cart/change.js`;
  }

  normalizePath(path) {
    return String(path || '').replace(/\/+$/, '') || '/';
  }

  isCartPage() {
    const currentPath = this.normalizePath(window.location.pathname);
    const cartPath = this.normalizePath(new URL(this.cartUrl, window.location.origin).pathname);
    return currentPath === cartPath;
  }

  findCartAnchor() {
    const selectors = [
      '[data-testid="cart-drawer-trigger"]',
      '.header-actions__action.action__cart',
      '.header-actions__action[aria-describedby="cart-bubble-text"]',
      '.header-actions__cart-icon'
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (!element) continue;

      if (element.classList.contains('header-actions__cart-icon')) {
        return element.closest('button, a') || element;
      }

      return element;
    }

    return null;
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  updateAnchorPosition() {
    const anchor = this.findCartAnchor();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const desktopWidth = Math.min(332, viewportWidth - 24);

    let desktopTop = 84;
    let desktopRight = 16;
    let desktopTriangleRight = 24;

    let mobileTop = 0;
    let mobileTriangleRight = 18;

    if (anchor) {
      const rect = anchor.getBoundingClientRect();
      const anchorVisible = rect.bottom > 0 && rect.top < viewportHeight;

      if (anchorVisible) {
        const anchorCenterX = rect.left + (rect.width / 2);

        desktopTop = Math.max(12, rect.bottom + 8);
        desktopRight = Math.max(12, viewportWidth - rect.right);
        desktopTriangleRight = this.clamp(
          (viewportWidth - anchorCenterX) - desktopRight,
          18,
          desktopWidth - 18
        );

        mobileTop = Math.max(0, rect.bottom + 4);
        mobileTriangleRight = this.clamp(
          viewportWidth - anchorCenterX,
          18,
          viewportWidth - 18
        );
      }
    }

    this.root.style.setProperty('--bag-notification-top', `${desktopTop}px`);
    this.root.style.setProperty('--bag-notification-right', `${desktopRight}px`);
    this.root.style.setProperty('--bag-notification-triangle-right', `${desktopTriangleRight}px`);
    this.root.style.setProperty('--bag-notification-mobile-top', `${mobileTop}px`);
    this.root.style.setProperty('--bag-notification-mobile-triangle-right', `${mobileTriangleRight}px`);
  }

  handleWindowReposition(event) {
    /*
      On mobile, while the bag notification is open, do not keep chasing the cart
      icon on scroll. The homepage sticky header can change position/state while
      scrolling, which makes the notification jump/glitch.
    */
    if (
      event?.type === 'scroll' &&
      this.mobileMediaQuery.matches &&
      this.root.classList.contains('is-open')
    ) {
      return;
    }

    this.updateAnchorPosition();
  }

  async fetchCart() {
    const response = await fetch(this.cartJsonUrl, {
      headers: {
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error('Unable to fetch cart');
    }

    return response.json();
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, this.moneyFormat);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency: this.currency,
    }).format((cents || 0) / 100);
  }

  getDisplayItemCount(cart) {
    let itemCount = Number(cart?.item_count || 0);

    for (const item of cart?.items || []) {
      const quantity = Number(item?.quantity || 0);
      const isSingleCountDonation =
        item?.properties?._donation_mode === 'custom' || item?.properties?._donation_type === 'rounded';

      if (item?.sku === 'DON' && isSingleCountDonation && quantity > 1) {
        itemCount = itemCount - quantity + 1;
      }
    }

    return Math.max(itemCount, 0);
  }

  syncHeaderCartBubble(cart) {
  const itemCount = this.getDisplayItemCount(cart);
  const visibleCount = itemCount > 99 ? '99+' : String(itemCount);

  // Keep Horizon's temporary cart-count cache in sync.
  try {
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(itemCount),
        timestamp: Date.now(),
      })
    );
  } catch (_) {
    // Storage may be unavailable; the visible cart bubble can still update.
  }

  const cartIcons = document.querySelectorAll('.header-actions__cart-icon');
    cartIcons.forEach((icon) => {
      icon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    });

    const bubbles = document.querySelectorAll('.cart-bubble');

    bubbles.forEach((bubble) => {
      bubble.classList.toggle('visually-hidden', itemCount < 1);

      if (itemCount > 0 && itemCount <= 99) {
        bubble.setAttribute('data-maintain-ratio', '');
      } else {
        bubble.removeAttribute('data-maintain-ratio');
      }

      const countNode = bubble.querySelector('.cart-bubble__text-count');
      if (countNode) {
        countNode.textContent = itemCount > 0 ? visibleCount : '';
        countNode.classList.toggle('hidden', itemCount < 1);
        countNode.setAttribute('aria-hidden', 'true');
      }

      const srNode = bubble.querySelector('.cart-bubble__text > .visually-hidden');
      if (srNode) {
        srNode.textContent = `Cart count: ${itemCount}`;
      }
    });
  }

  itemCountLabel(count) {
    return `${count} item${count === 1 ? '' : 's'}`;
  }

  readRecentKeys() {
    try {
      const raw = window.sessionStorage.getItem(this.storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  writeRecentKeys(keys) {
    try {
      window.sessionStorage.setItem(this.storageKey, JSON.stringify(keys.slice(0, 50)));
    } catch (error) {
      // sessionStorage can fail silently in restricted contexts
    }
  }

  cleanupRecentKeys(cart) {
    const validKeys = new Set((cart?.items || []).map((item) => item.key));
    const cleaned = this.readRecentKeys().filter((key) => validKeys.has(key));
    this.writeRecentKeys(cleaned);
  }

  updateRecentKeysFromDiff(previousCart, currentCart) {
    const previousQuantities = new Map(
      (previousCart?.items || []).map((item) => [item.key, Number(item.quantity || 0)])
    );

    const touchedKeys = [];

    for (const item of currentCart?.items || []) {
      const previousQuantity = previousQuantities.get(item.key) || 0;
      const currentQuantity = Number(item.quantity || 0);

      if (currentQuantity > previousQuantity) {
        touchedKeys.push(item.key);
      }
    }

    if (!touchedKeys.length && !previousCart && currentCart?.items?.length) {
      const newest = currentCart.items[currentCart.items.length - 1];
      if (newest?.key) touchedKeys.push(newest.key);
    }

    const existing = this.readRecentKeys();
    const merged = [...touchedKeys.reverse(), ...existing.filter((key) => !touchedKeys.includes(key))];
    this.writeRecentKeys(merged);
  }

  getSortedItems(cart) {
    const recentKeys = this.readRecentKeys();
    const recentIndex = new Map(recentKeys.map((key, index) => [key, index]));

    return (cart.items || [])
      .map((item, index) => ({
        ...item,
        line: index + 1,
      }))
      .sort((a, b) => {
        const aIndex = recentIndex.has(a.key) ? recentIndex.get(a.key) : Number.MAX_SAFE_INTEGER;
        const bIndex = recentIndex.has(b.key) ? recentIndex.get(b.key) : Number.MAX_SAFE_INTEGER;

        if (aIndex !== bIndex) return aIndex - bIndex;
        return a.line - b.line;
      });
  }

  buildItemElement(item) {
    const itemElement = document.createElement('div');
    itemElement.className = 'bag-notification__item';

    const mediaLink = document.createElement('a');
    mediaLink.className = 'bag-notification__item-media';
    mediaLink.href = item.url || this.cartUrl;

    const image = document.createElement('img');
    image.className = 'bag-notification__item-image';
    image.loading = 'lazy';
    image.alt = item.product_title || item.title || '';
    image.src = item.image || item.featured_image?.url || '';
    mediaLink.appendChild(image);

    const content = document.createElement('div');
    content.className = 'bag-notification__item-content';

    const price = document.createElement('div');
    price.className = 'bag-notification__item-price';
    price.textContent = this.formatMoney(
      Number(item.final_price ?? item.price ?? item.original_price ?? 0)
    );

    const title = document.createElement('a');
    title.className = 'bag-notification__item-title';
    title.href = item.url || this.cartUrl;
    title.textContent = item.product_title || item.title || '';

    const meta = document.createElement('div');
    meta.className = 'bag-notification__item-meta';

    const metaParts = [];
    if (item.variant_title) metaParts.push(item.variant_title);
    if (item.selling_plan_allocation?.selling_plan?.name) {
      metaParts.push(item.selling_plan_allocation.selling_plan.name);
    }

    meta.textContent = metaParts.join(' • ');

    const footer = document.createElement('div');
    footer.className = 'bag-notification__item-footer';

    const qty = document.createElement('span');
    qty.className = 'bag-notification__item-qty';
    qty.textContent = `Qty: ${item.quantity || 1}`;

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'bag-notification__item-remove button button--tertiary cart-items__remove';
    removeButton.setAttribute('aria-label', `Remove ${item.product_title || item.title || 'item'}`);
    removeButton.setAttribute('data-bag-remove-line', String(item.line));
    removeButton.innerHTML = `${this.deleteIconMarkup}<span class="visually-hidden">Remove</span>`;

    content.appendChild(price);
    content.appendChild(title);

    if (meta.textContent) {
      content.appendChild(meta);
    }

    footer.appendChild(qty);
    footer.appendChild(removeButton);
    content.appendChild(footer);

    itemElement.appendChild(mediaLink);
    itemElement.appendChild(content);

    return itemElement;
  }

  renderDelivery(totalPrice) {
    const updateDeliveryDisplay = (wrap, messageElement, progressBar) => {
      if (!wrap || !messageElement || !progressBar) return;

      if (totalPrice < this.freeDeliveryShowFrom) {
        wrap.classList.add('hidden');
        messageElement.textContent = '';
        progressBar.style.width = '0%';
        return;
      }

      wrap.classList.remove('hidden');

      if (totalPrice >= this.freeDeliveryThreshold) {
        messageElement.textContent = 'You qualify for FREE UK delivery!';
        progressBar.style.width = '100%';
        return;
      }

      const remaining = this.freeDeliveryThreshold - totalPrice;
      const range = this.freeDeliveryThreshold - this.freeDeliveryShowFrom;
      const progress = Math.max(
        0,
        Math.min(100, ((totalPrice - this.freeDeliveryShowFrom) / range) * 100)
      );

      messageElement.textContent = `You are ${this.formatMoney(remaining)} away from FREE UK delivery!`;
      progressBar.style.width = `${progress}%`;
    };

    updateDeliveryDisplay(this.deliveryWrap, this.deliveryElement, this.deliveryProgressBar);
    updateDeliveryDisplay(this.mobileDeliveryWrap, this.mobileDeliveryElement, this.mobileDeliveryProgressBar);
  }

  render(cart) {
    const sortedItems = this.getSortedItems(cart);
    const displayItemCount = this.getDisplayItemCount(cart);

    if (this.countElement) {
      this.countElement.textContent = this.itemCountLabel(displayItemCount);
    }

    if (this.subtotalElement) {
      this.subtotalElement.textContent = this.formatMoney(Number(cart.total_price || 0));
    }

    this.renderDelivery(Number(cart.total_price || 0));

    if (this.itemsContainer) {
      this.itemsContainer.innerHTML = '';
      sortedItems.forEach((item) => {
        this.itemsContainer.appendChild(this.buildItemElement(item));
      });
    }

    this.syncHeaderCartBubble(cart);
  }

  clearCloseTimer() {
    window.clearTimeout(this.timer);
    this.timer = null;
  }

  scheduleClose() {
    this.clearCloseTimer();

    if (this.isHoveredOrFocused) return;

    this.timer = window.setTimeout(() => {
      this.close();
    }, this.mobileMediaQuery.matches ? this.mobileTimeout : this.desktopTimeout);
  }

  open() {
    this.updateAnchorPosition();

    this.root.classList.add('is-open');
    this.root.setAttribute('aria-hidden', 'false');

    document.documentElement.classList.add('bag-notification-open');
    document.body.classList.add('bag-notification-open');

    if (this.mobileMediaQuery.matches) {
      this.mobileMinimumVisibleUntil = Date.now() + 3500;
    } else {
      this.mobileMinimumVisibleUntil = 0;
    }

    this.scheduleClose();
  }

  close() {
    this.clearCloseTimer();

    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');

    document.documentElement.classList.remove('bag-notification-open');
    document.body.classList.remove('bag-notification-open');

    this.isHoveredOrFocused = false;
    this.mobileMinimumVisibleUntil = 0;
  }

  handleMouseEnter() {
    this.isHoveredOrFocused = true;
    this.clearCloseTimer();
  }

  handleMouseLeave() {
    this.isHoveredOrFocused = false;
    this.scheduleClose();
  }

  handleFocusIn() {
    this.isHoveredOrFocused = true;
    this.clearCloseTimer();
  }

  handleFocusOut() {
    const active = document.activeElement;
    const insideDesktop = this.desktopPanel?.contains(active);
    const insideMobile = this.mobilePanel?.contains(active);

    if (insideDesktop || insideMobile) return;

    this.isHoveredOrFocused = false;
    this.scheduleClose();
  }

  handleDocumentPointerDown(event) {
    if (!this.root.classList.contains('is-open')) return;
    if (this.root.contains(event.target)) return;

    if (this.mobileMediaQuery.matches && Date.now() < this.mobileMinimumVisibleUntil) {
      return;
    }

    this.close();
  }

  async handleCartAdd(event) {
  const eventCart = event?.detail?.resource;

  const cart =
    eventCart &&
    Array.isArray(eventCart.items) &&
    typeof eventCart.item_count === 'number'
      ? eventCart
      : await this.fetchCart().catch(() => null);

  if (!cart) return;

    this.updateRecentKeysFromDiff(this.previousCart, cart);
    this.cleanupRecentKeys(cart);
    this.previousCart = cart;

    if (Number(cart.item_count || 0) === 0) {
  this.close();
  this.syncHeaderCartBubble(cart);
  return;
}

    this.render(cart);

    // Do not auto-open when already on the cart page
    if (this.isCartPage()) {
      return;
    }

    // Do not auto-open from cart page / cart item edits
    const source = event?.detail?.data?.source || '';
    if (source === 'cart-items-component') {
      return;
    }

    this.open();
  }

  async handleCartUpdate() {
    const cart = await this.fetchCart().catch(() => null);
    if (!cart) return;

    this.cleanupRecentKeys(cart);
    this.previousCart = cart;

    if (Number(cart.item_count || 0) === 0) {
      this.close();
      this.syncHeaderCartBubble(cart);
      return;
    }

    // Keep content in sync if already open, but do not auto-open on cart updates
    if (this.root.classList.contains('is-open')) {
      this.render(cart);
      this.updateAnchorPosition();
    } else {
      this.syncHeaderCartBubble(cart);
    }
  }

  async removeLine(line) {
    const parsedLine = Number(line);
    if (!parsedLine || parsedLine < 1) return;

    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent && typeof cartItemsComponent.onLineItemRemove === 'function') {
      cartItemsComponent.onLineItemRemove(parsedLine);
      return;
    }

    await fetch(this.fallbackCartChangeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        line: parsedLine,
        quantity: 0,
      }),
    }).catch(() => null);

    const cart = await this.fetchCart().catch(() => null);
    if (!cart) return;

    this.cleanupRecentKeys(cart);
    this.previousCart = cart;

    if (Number(cart.item_count || 0) === 0) {
      this.close();
      this.syncHeaderCartBubble(cart);
      return;
    }

    this.render(cart);
  }

  handleClick(event) {
    const removeButton = event.target.closest('[data-bag-remove-line]');
    if (!removeButton) return;

    event.preventDefault();
    this.removeLine(removeButton.getAttribute('data-bag-remove-line'));
  }
}

const root = document.getElementById('bag-notification');

if (root) {
  const notification = new BagNotification(root);
  notification.init();
}